import { Dispatch, SetStateAction } from "react";

import moment from "moment";

import { colors } from "@dotnet/telematics-design-system";

import { DATE_FORMATS, LANGUAGES } from "../../../../shared";
import { emptyDoughnutStyles } from "../../config";
import { TooltipDataType } from "../types";
import { checkIfArrayHasAnyValues, generateBoldTickElement } from "./functions";

export const DOUGHNUT_DIAMETER = "142";
export const DEMO_CREDITS_COUNT = 30;

export const options: any = (_data: any, setHoveredElement?: Dispatch<any>) => {
  const cutout = 48;

  return {
    responsive: true,
    maintainAspectRatio: false,
    onHover: function (_event: any, activeElements: any) {
      if (!setHoveredElement) return;

      if (activeElements.length > 0) {
        setHoveredElement(activeElements[0].index);
      }
    },
    plugins: {
      tooltip: {
        // disable default tooltip
        enabled: false
      },
      legend: {
        display: false
      },
      emptyDoughnut: emptyDoughnutStyles(cutout)
    },
    cutout
  };
};

let setTooltipData: (tooltip: TooltipDataType | null) => void;

export const attachTooltipUpdater = (cb: typeof setTooltipData) => {
  setTooltipData = cb;
};

let lastTooltipData: TooltipDataType | null = null;

const elementInArray = (numberToSubtract: number, monthsArray?: string[]) =>
  monthsArray?.[monthsArray?.length - numberToSubtract].slice(0, 3);

const externalTooltip = (
  context: any,
  language: LANGUAGES,
  monthsArray?: string[],
  setHovered?: Dispatch<SetStateAction<boolean>>
) => {
  const tooltip = context.tooltip;

  setHovered?.(tooltip.opacity === 1);

  if (!tooltip || tooltip.opacity === 0) {
    if (lastTooltipData !== null) {
      setTooltipData(null);
      lastTooltipData = null;
    }
    return;
  }

  const dp = tooltip.dataPoints?.[0];
  if (!dp) return;

  const index = dp.dataIndex;

  const rawData = dp.dataset?.labels?.[index];
  if (!rawData) return;

  const dateInLocale = moment(rawData.date).locale(language);

  const shortDateName = dateInLocale.format(DATE_FORMATS.MonthLong).slice(0, 3);

  const isLastElementInchart = monthsArray
    ? index === monthsArray.length - 1
    : false;

  const isFirstElementInChart = shortDateName === monthsArray?.[0].slice(0, 3);

  const tooltipDate = () => {
    return dateInLocale.format(DATE_FORMATS.MonthDayYearLong);
  };

  const canvasRect = context.chart.canvas.getBoundingClientRect();
  const tooltipXAlignment =
    canvasRect.left +
    window.scrollX +
    tooltip.caretX -
    leftAlignment(isLastElementInchart, isFirstElementInChart);

  const newTooltipData: TooltipDataType = {
    visible: true,
    x: tooltipXAlignment,
    y: canvasRect.top + window.scrollY + tooltip.caretY - 110,
    label: tooltip.body?.[0]?.lines[0] ?? "",
    date: tooltipDate(),
    isLastElementInchart,
    deducted: rawData.deducted,
    added: rawData.added,
    reserved: rawData.reserved
  };

  const tooltipChanged =
    JSON.stringify(lastTooltipData) !== JSON.stringify(newTooltipData);
  if (tooltipChanged) {
    setTooltipData(newTooltipData);
    lastTooltipData = newTooltipData;
  }
};

const styleLinePointsBackground = (ctx: any, monthsArray?: string[]) => {
  return monthsArray && ctx.index === monthsArray?.length - 1
    ? colors.blue600
    : colors.white;
};

export const lineChartOptions = (
  language: LANGUAGES,
  monthsArray?: string[],
  monthlyData?: number[],
  setHovered?: Dispatch<SetStateAction<boolean>>
) => {
  const chartFontSize = 10;
  const fontFamily = '"Inter", system-ui';

  return {
    responsive: true,
    // line below enables possibility to change height of chart
    maintainAspectRatio: false,
    scales: {
      x: {
        afterUpdate: (axis: any) => {
          axis.marginRight = 0;
        },
        ticks: {
          stepSize: 1,
          font: {
            size: chartFontSize,
            weight: generateBoldTickElement(monthsArray),
            family: fontFamily
          },
          color: colors.textSecondary
        },
        grid: {
          display: false,
          drawBorder: false
        },
        border: {
          display: false
        }
      },
      y: {
        grace: 0,
        // line below removes padding from top of ticks labels
        afterUpdate: (axis: any) => {
          axis.paddingTop = monthsArray && monthsArray?.length > 0 ? 0 : 5;
        },
        ticks: {
          font: {
            size: chartFontSize,
            weight: 400,
            family: fontFamily
          },
          color: colors.textSecondary,
          // line below add padding around of ticks labels
          padding: 10
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          color: colors.gray100
        },
        border: {
          display: false
        },
        beginAtZero: true,
        max: checkIfArrayHasAnyValues(monthlyData) ? undefined : 300
      }
    },
    elements: {
      point: {
        borderWidth: 2,
        backgroundColor: (ctx: any) =>
          styleLinePointsBackground(ctx, monthsArray)
      }
    },
    plugins: {
      legend: {
        display: false
      },

      tooltip: {
        enabled: false,

        external: (context: any) =>
          externalTooltip(context, language, monthsArray, setHovered)
      },

      lineChartAreaBorder: {
        borderColor: colors.gray100,
        borderWidth: 1,
        borderline: 6
      }
    }
  };
};

export const lineChartAreaBorder = {
  id: "lineChartAreaBorder",
  beforeDraw(chart: any, _args: any, options: any) {
    const {
      ctx,
      chartArea: { left, top, width, height }
    } = chart;
    ctx.save();
    ctx.strokeStyle = options.borderColor;
    ctx.lineWidth = options.borderWidth;
    ctx.strokeRect(left, top, width, height);
    ctx.restore();
  }
};

const leftAlignment = (
  isPenultimateElementIChart: boolean,
  isFirstElementIChart: boolean
): number => {
  if (isPenultimateElementIChart) {
    return isFirstElementIChart ? 50 : 100;
  }

  if (isFirstElementIChart) return 50;

  return 88;
};
